import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { getLogs } from "./api/get-logs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Search } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

export default function LogAktivitasPage() {
  const [startDate, setStartDate] = useState(() => {
    return new Date().toISOString().split("T")[0];
  });
  const [endDate, setEndDate] = useState(() => {
    return new Date().toISOString().split("T")[0];
  });

  const { data, isLoading, refetch, isRefetching } = useQuery({
    queryKey: ["activity-logs", startDate, endDate],
    queryFn: () => getLogs({ startDate, endDate }),
  });

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    refetch();
  };

  const formatJSON = (str: string) => {
    try {
      const obj = JSON.parse(str);
      return JSON.stringify(obj, null, 2);
    } catch {
      return str;
    }
  };

  const logs = Array.isArray(data?.datas)
    ? data.datas
    : Array.isArray(data?.data)
      ? data.data
      : Array.isArray(data)
        ? data
        : [];

  return (
    <div className="flex-1 space-y-4 p-4 md:p-8 pt-6">
      <div className="flex items-center justify-between space-y-2 mb-4">
        <h2 className="text-3xl font-bold tracking-tight">
          Log Aktivitas Sistem
        </h2>
      </div>

      <div className="bg-card p-4 rounded-xl border border-border/40 shadow-sm mb-6">
        <form
          onSubmit={handleSearch}
          className="flex flex-col sm:flex-row gap-4 items-end"
        >
          <div className="space-y-2 flex-1 max-w-50">
            <Label htmlFor="startDate">Tanggal Mulai</Label>
            <Input
              id="startDate"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              required
            />
          </div>
          <div className="space-y-2 flex-1 max-w-50">
            <Label htmlFor="endDate">Tanggal Akhir</Label>
            <Input
              id="endDate"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              required
            />
          </div>
          <Button type="submit" disabled={isLoading || isRefetching}>
            {isLoading || isRefetching ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Search className="w-4 h-4 mr-2" />
            )}
            Cari
          </Button>
        </form>
      </div>

      <div className="bg-card border border-border/40 rounded-xl overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader className="bg-muted/50">
              <TableRow>
                <TableHead className="w-45">Waktu</TableHead>
                <TableHead className="w-25">Status</TableHead>
                <TableHead className="w-50">Request / IP</TableHead>
                <TableHead>Detail Log</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={4} className="h-32 text-center">
                    <div className="flex justify-center items-center h-full">
                      <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                    </div>
                  </TableCell>
                </TableRow>
              ) : logs && logs.length > 0 ? (
                logs.map((log: any, index: number) => (
                  <TableRow key={log.id || index}>
                    <TableCell className="font-medium whitespace-nowrap">
                      {log.incident_time
                        ? new Date(log.incident_time).toLocaleString("id-ID")
                        : "-"}
                    </TableCell>
                    <TableCell>
                      {log.log_status ? (
                        <Badge
                          variant={
                            log.log_status.toUpperCase() === "ERROR"
                              ? "destructive"
                              : "secondary"
                          }
                        >
                          {log.log_status.toUpperCase()}
                        </Badge>
                      ) : (
                        "-"
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col">
                        <span className="font-mono text-xs font-semibold">
                          {log.user_request || "-"}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          {log.ip || "-"}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Dialog>
                        <DialogTrigger asChild>
                          <div
                            className="text-xs font-mono max-w-75 truncate cursor-pointer hover:text-primary hover:underline transition-colors"
                            title="Klik untuk melihat detail"
                          >
                            {log.log_details || "-"}
                          </div>
                        </DialogTrigger>
                        <DialogContent className="max-w-6xl w-[95vw] max-h-[85vh] overflow-y-auto">
                          <DialogHeader>
                            <DialogTitle>Detail Log Aktivitas</DialogTitle>
                          </DialogHeader>
                          <div className="bg-muted p-4 rounded-md mt-2">
                            <pre className="text-xs font-mono whitespace-pre-wrap wrap-break-word">
                              {log.log_details
                                ? formatJSON(log.log_details)
                                : "-"}
                            </pre>
                          </div>
                        </DialogContent>
                      </Dialog>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={4}
                    className="h-32 text-center text-muted-foreground"
                  >
                    Tidak ada log aktivitas pada rentang tanggal tersebut.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}
